import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { BASE_URL, DEFAULT_VERSION } from '@/lib/constants';
import { createUploadLink } from 'apollo-upload-client';
import { getAppPlatform } from '@/lib/platform';
import * as Sentry from '@sentry/capacitor';
import * as SentryVue from '@sentry/vue';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';

if (import.meta.env.DEV) {
    loadDevMessages();
    loadErrorMessages();
}

const graphQLEndpoint = BASE_URL;

if (!graphQLEndpoint) {
  throw new Error('VITE_API_URL is not configured.');
}

const uploadLink = createUploadLink({
  uri: `${graphQLEndpoint}/graphql`,
  credentials: 'include',
  fetchOptions: {
    credentials: 'include',
  },
});

let currentAuthToken: string | null = null;
let currentLocale: string = 'en';
let currentVersion: string = '';
const currentPlatform: string = getAppPlatform();

function getXsrfToken(): string | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const authLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    ...(currentAuthToken ? { Authorization: `Bearer ${currentAuthToken}` } : {}),
    ...(getAppPlatform() === 'web' && getXsrfToken()
      ? { 'X-XSRF-TOKEN': getXsrfToken()! }
      : {}),
    'Accept-Language': currentLocale,
    'X-App-Version': currentVersion || DEFAULT_VERSION,
    'X-App-Platform': currentPlatform,
  },
}));

const sentryBreadcrumbLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);
  Sentry.addBreadcrumb({
    category: 'graphql.' + (definition.kind === 'OperationDefinition' ? definition.operation : '??'),
    message: operation.operationName,
    data: operation.variables,
    level: 'info',
  });
  return forward(operation);
});

const sentryErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const definition = getMainDefinition(operation.query);
  const operationType = definition.kind === 'OperationDefinition' ? definition.operation : 'unknownOperation';
  const category = `graphql.${operationType}.${operation.operationName}`;

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, extensions }) => {
      SentryVue.addBreadcrumb({
        category,
        message: `[GraphQL error] ${message}`,
        data: { variables: operation.variables, locations, extensions },
        level: 'error',
      });
    });
  }

  if (networkError) {
    SentryVue.addBreadcrumb({
      category,
      message: `[Network error] ${networkError}`,
      data: { variables: operation.variables },
      level: 'error',
    });
  }

  if (import.meta.env.DEV) {
    // logErrorMessages is useful but optional — install @vue/apollo-util if you want it
    console.error('[Apollo Error]', { graphQLErrors, networkError });
  } else {
    console.error(JSON.stringify({ graphQLErrors, networkError }));
  }
});

export function setAuthToken(token?: string | null) {
  currentAuthToken = token ?? null;
}

export function setLocale(locale: string) {
  currentLocale = locale;
}

export function setVersion(version: string) {
  currentVersion = version;
}

const apolloLinkChain: ApolloLink = ApolloLink.from([
  sentryBreadcrumbLink,
  sentryErrorLink,
  authLink,
  uploadLink as unknown as ApolloLink,
]);

export const apolloClient = new ApolloClient({
  link: apolloLinkChain,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          plotPosts: relayStylePagination(['plotSlug']),
        },
      },
    },
  }),
});
